import * as _ from "lodash";
import React, { useState } from "react";
import { Accordion } from "../../../../common/components/accordion/Accordion";
import { Overlay } from "../../../../common/components/modals/Overlay";
import useModal from "../../../../common/components/modals/useModal";
import { newErrorToast, TOAST_QUICK_SEARCH_DEFINE_FIVE } from "../../../../common/components/toasts/constants";
import useToasts from "../../../../common/components/toasts/useToasts";
import useSearchState from "../../../useSearchState";
import { CustomizeQuickSearchGroup } from "./CustomizeQuickSearchGroup";
import { useCustomizeQuickSearch } from "./useCustomizeQuickSearch";
import { criteriaGroupsType, customizeQuickSearchType } from "../../../../common/types/search/search";
import Immutable from "immutable";

interface CustomizeQuickSearchProps {
	criteria: criteriaGroupsType;
	customizeQuickSearch: customizeQuickSearchType;
	namespace: string;
	quickSearchCriteriaOrder?: { [key: string]: number };
}

export function CustomizeQuickSearch({
	criteria,
	customizeQuickSearch,
	namespace,
	quickSearchCriteriaOrder,
}: CustomizeQuickSearchProps) {
	const { currentInput } = useSearchState();
	const {
		input,
		selectedCriteria,
		criteriaWithVisibilitySelectors,
		onInputChange,
		onSelectedCriteriaChange,
		resetCriteria,
		updateCriteria,
	} = useCustomizeQuickSearch(namespace, currentInput, criteria);
	const { clearModals } = useModal();
	const [loading, setLoading] = useState(false);
	const [deleting, setDeleting] = useState(false);
	const { onAddToast } = useToasts();
	const radios = customizeQuickSearch.radios || [];

	const selectedCriteriaCount = selectedCriteria.size;
	const maxCountOfCriteriaSelected = selectedCriteriaCount === 5;

	const handleSelectedCriteriaChange = (criteria: Immutable.List<string>) => {
		onSelectedCriteriaChange(
			quickSearchCriteriaOrder
				? criteria.sort(
						(a: string, b: string) =>
							quickSearchCriteriaOrder[a] -
							quickSearchCriteriaOrder[b],
					)
				: criteria,
		);
	};

	const handleRadioChange = (input: Immutable.List<string>) => {
		let newInput = selectedCriteria;

		// Uncheck checkboxes that are not visible after radio change
		if (criteriaWithVisibilitySelectors.length > 0) {
			criteriaWithVisibilitySelectors.forEach((criterion) => {
				const { key, visibilitySelector } = criterion;
				const { criterionKey, visibleOn } = visibilitySelector;

				if (
					selectedCriteria.includes(key) &&
					input.get(criterionKey) !== visibleOn
				) {
					const index = newInput.indexOf(key);
					newInput = newInput.delete(index);
				}
			});
		}

		handleSelectedCriteriaChange(newInput);
		onInputChange(input);
	};

	const handleSaveCriteria = () => {
		if (selectedCriteriaCount !== 5) {
			onAddToast(TOAST_QUICK_SEARCH_DEFINE_FIVE);
		} else {
			setLoading(true);

			const successHandler = () => {
				setLoading(false);
				clearModals();
			};

			const failureHandler = (error: { message: string }) => {
				onAddToast(
					newErrorToast(_.camelCase(error.message), error.message),
				);
				setLoading(false);
			};

			updateCriteria(
				selectedCriteria,
				input,
				successHandler,
				failureHandler,
			);
		}
	};

	const handleResetCriteria = () => {
		setDeleting(true);

		const successHandler = () => {
			setDeleting(false);
			clearModals();
		};

		const failureHandler = (error: { message: string }) => {
			onAddToast(
				newErrorToast(_.camelCase(error.message), error.message),
			);
			setDeleting(false);
		};

		resetCriteria(successHandler, failureHandler);
	};

	return (
		<Overlay
			title="Customise filters"
			description={`Add or remove 5 filters to your quick search. ${selectedCriteriaCount} of 5 filters selected.`}
			additionalClasses="customize-quick-search"
		>
			<div className="search-criteria modal__content">
				<Accordion>
					{criteria.map((group) => {
						const groupRadios = radios.filter((radio) =>
							Object.keys(group.criteria).includes(radio),
						);
						return (
							<CustomizeQuickSearchGroup
								key={group.name}
								group={group}
								input={input}
								onRadioChange={handleRadioChange}
								onSelectedCriteriaChange={
									handleSelectedCriteriaChange
								}
								radios={groupRadios}
								maxCountOfCriteriaSelected={
									maxCountOfCriteriaSelected
								}
								selectedCriteria={selectedCriteria}
							/>
						);
					})}
				</Accordion>
			</div>

			<footer className="modal__actions">
				<div className="actions__container">
					<div className="actions__group">
						<button
							type="button"
							className="btn btn--tertiary"
							onClick={handleResetCriteria}
						>
							{deleting ? "Resetting..." : "Reset to default"}
						</button>
						<button
							type="button"
							className="btn btn--primary"
							onClick={handleSaveCriteria}
						>
							{loading ? "Saving..." : "Save"}
						</button>
					</div>
				</div>
			</footer>
		</Overlay>
	);
}
